import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Badge, Button, Checkbox, EmptyState, Field, Mono, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { formatDateTime, formatNumber } from '@/lib/format';
import { apiCallsQuery } from '@/features/admin/queries';

const search = z.object({ channel: z.enum(['rest', 'mcp']).optional(), errors: z.boolean().optional() });

export const Route = createFileRoute('/_app/admin/api-calls')({ validateSearch: search, component: ApiCalls });

function ApiCalls() {
  const { t, i18n } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useInfiniteQuery(apiCallsQuery({ channel: s.channel, errorsOnly: s.errors || undefined }));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const set = (patch: Partial<z.infer<typeof search>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  return (
    <>
      <PageHeader eyebrow={t('nav.admin')} title={t('apiCalls.title')} description={t('apiCalls.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('apiCalls.channel')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.channel ?? ''} onChange={(e) => set({ channel: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              <option value="rest">REST</option>
              <option value="mcp">MCP</option>
            </NativeSelect>
          )}
        </Field>
        <label className="flex h-10 items-center gap-2 text-sm">
          <Checkbox checked={!!s.errors} onCheckedChange={(v) => set({ errors: v === true || undefined })} />
          {t('apiCalls.errorsOnly')}
        </label>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('audit.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('audit.when')}</Th>
              <Th>{t('apiCalls.channel')}</Th>
              <Th>{t('apiCalls.tool')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="text-right">{t('apiCalls.duration')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((r) => (
              <Tr key={r.id}>
                <Td className="whitespace-nowrap tabular text-fg-2">{formatDateTime(r.occurredAt)}</Td>
                <Td>
                  <Badge tone="outline">{r.channel.toUpperCase()}</Badge> <span className="text-xs text-fg-3">{r.authMethod}</span>
                </Td>
                <Td>
                  <Mono className="text-xs">{r.tool ?? `${r.method} ${r.path}`}</Mono>
                </Td>
                <Td>
                  <Badge tone={r.status >= 500 ? 'danger' : r.status >= 400 ? 'warning' : 'success'}>
                    {r.status}
                    {r.errorCode && ` ${r.errorCode}`}
                  </Badge>
                </Td>
                <Td className="text-right tabular">{formatNumber(r.durationMs, i18n.language as 'nl')} ms</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}
